import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { HiCheckCircle, HiInformationCircle, HiLink, HiSparkles } from "react-icons/hi";
import CustomButton from '../../../../components/common/CustomButton';
import FormInput from '../../../../components/common/FormInput';
import GithubSettingsSkeleton from "./GithubSettingsSkeleton";
import { useOrganizationService } from "../../../../services";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import { useIntegrationStatus } from "../../../../hooks/useIntegrationStatus";
import UnsavedChangesIndicator from '../../../../components/common/UnsavedChangesIndicator';

interface GithubSettingsProps {
  handleClose: () => void;
  handleActivationCallback?: () => void;
}

const GithubSettings: React.FC<GithubSettingsProps> = ({ handleClose, handleActivationCallback }) => {
  const { isIntegrationActive: checkIntegrationActive } = useIntegrationStatus();
  const isGithubActive = checkIntegrationActive('github');

  const [step, setStep] = useState(isGithubActive ? 2 : 1);
  const [pat, setPat] = useState('');
  const [isIntegrationActive, setIsIntegrationActive] = useState(false);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [initialValues, setInitialValues] = useState({ pat: '', isIntegrationActive: false });
  const { githubSettingsLoading } = useSelector((state: any) => state.organization);
  const { addGithubIntegration, getGithubSettings, updateGithubIntegration } = useOrganizationService();

  useEffect(() => {
    getGithubSettings().then((res: any) => {
      const data = res?.data || {};
      const patFromSettings = data.github_pat || '';
      const activeFromSettings = Boolean(data.is_active);

      setPat(patFromSettings);
      setInitialValues({ pat: patFromSettings, isIntegrationActive: activeFromSettings });
      setIsIntegrationActive(activeFromSettings);
      setIsFirstTimeSetup(!patFromSettings);

      if (activeFromSettings) {
        setStep(2);
      } else {
        setStep(1);
      }
    });
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (pat !== initialValues.pat) return true;
    if (isIntegrationActive !== initialValues.isIntegrationActive) return true;
    return false;
  }, [pat, isIntegrationActive, initialValues]);

  const handleConnect = () => {
    if (!pat) {
      toast.error("Please enter a Personal Access Token.");
      return;
    }

    setIsConnecting(true);

    if (isFirstTimeSetup) {
      addGithubIntegration({ github_pat: pat, is_active: true })
        .then(() => {
          setInitialValues({ pat, isIntegrationActive: true });
          setIsIntegrationActive(true);
          setStep(2);
          setIsFirstTimeSetup(false);
          handleActivationCallback?.();
        })
        .catch((error: any) => {
          console.error("Failed to connect:", error);
        })
        .finally(() => {
          setIsConnecting(false);
        });
    } else {
      updateGithubIntegration({
        github_pat: pat,
        is_active: true
      })
        .then(() => {
          setInitialValues({ pat, isIntegrationActive: true });
          setIsIntegrationActive(true);
          setStep(2);
          handleActivationCallback?.();
        })
        .catch((error: any) => {
          console.error("Failed to connect:", error);
        })
        .finally(() => {
          setIsConnecting(false);
        });
    }
  };

  const handleSaveChanges = () => {
    updateGithubIntegration({ github_pat: pat, is_active: isIntegrationActive }).then(() => {
      setInitialValues({ pat, isIntegrationActive });
    });
  };

  const handleDisconnect = () => {
    setIsDisconnecting(true);
    updateGithubIntegration({ github_pat: pat, is_active: false })
      .then(() => {
        setIsIntegrationActive(false);
        setInitialValues({ pat, isIntegrationActive: false });
        setStep(1);
      })
      .finally(() => {
        setIsDisconnecting(false);
      });
  };

  return (
    <div className="bg-tertiary-light dark:bg-tertiary-dark w-full h-full relative flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5 dark:bg-tertiary-dark z-40 shadow-lg rounded-b-2xl shrink-0">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col min-w-0 max-w-full">
            <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
              GitHub Integration
            </div>
            <a
              href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token"
              target="_blank"
              rel="noreferrer"
              className="flex flex-row items-center gap-2 mt-2 text-light-text-tertiary dark:text-white/60 text-sm hover:text-sky-500 dark:hover:text-sky-200 transition-colors duration-200"
            >
              <HiLink className="w-4 h-4" />
              GitHub Personal Access Token Guide
            </a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white
              hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
              onClick={handleClose}>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {githubSettingsLoading || isConnecting || isDisconnecting ? (
        <GithubSettingsSkeleton step={isConnecting ? 2 : isDisconnecting ? 1 : step} />
      ) : (
        <div className="p-4 md:px-8 md:py-4 flex-1 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-6 max-w-3xl">
              {/* Main Info Card */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 shadow-sm">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 mt-2">
                    {/* Placeholder for GitHub Logo - using a div with icon if image not available, or just the icon */}
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-800 dark:text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                      Connect your GitHub account
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                      Integrate Cadence Pro with GitHub to sync repositories, track pull requests, and manage issues directly within your project workflows.
                    </p>
                  </div>
                </div>

                {/* Features List */}
                <div className="mb-6 pl-1">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    What you'll get:
                  </h3>
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Repository Access</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">View and link your GitHub repositories to projects</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Issue Tracking</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Sync GitHub issues with your task management board</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <HiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[13px] font-medium text-gray-900 dark:text-white">Pull Request Insights</p>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400">Monitor PR status and activity directly from Cadence Pro</p>
                      </div>
                    </div>
                  </div>
                </div>



                {/* Input Section */}
                <div className="mt-6 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-6">
                  <FormInput
                    id="pat"
                    name="pat"
                    label="Personal Access Token"
                    type="password"
                    value={pat}
                    onChange={(e) => setPat(e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </div>




              </div>

              {/* Info Box */}
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                      Required Permissions
                    </p>
                    <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed mb-2">
                      You need to generate a Personal Access Token (PAT) in GitHub with appropriate permissions.
                    </p>
                    <a
                      href="https://github.com/settings/tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold inline-flex items-center gap-1 hover:underline"
                    >
                      Generate Token on GitHub <HiLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connection Status */}
              <div className="flex items-center justify-between px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm text-green-800 dark:text-green-200 font-medium">Successfully connected to GitHub</p>
                  </div>
                </div>
                <div className={hasUnsavedChanges ? "invisible" : ""}>
                  <CustomButton variant="danger" onClick={handleDisconnect} size="sm" className="px-2">
                    Disconnect
                  </CustomButton>
                </div>
              </div>

              {/* Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                  Integration Settings
                </h3>
                <div className="space-y-4">
                  <FormInput
                    id="pat"
                    name="pat"
                    label="GitHub Personal Access Token"
                    type="password"
                    value={pat}
                    onChange={(e) => setPat(e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </div>
              </div>

              {/* Next Steps Guide */}
              {/* Next Steps Guide */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800/50 p-5">
                <h3 className="text-base font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                  <HiSparkles className="w-5 h-5 text-blue-500 hover:text-purple-500 transition-colors duration-200" />
                  Next Steps: Try asking Cadence AI
                </h3>
                <div className="grid gap-3">
                  <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-colors shadow-sm">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">Track Real-time Progress</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 italic">"What's the status of the 'Mobile App V2' work unit based on GitHub activity?"</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-colors shadow-sm">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">Monitor Developer Activity</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 italic">"Show me recent code check-ins for developer 'Jane Doe'."</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-500/50 transition-colors shadow-sm">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-1">Visualize Sprint Commit Data</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 italic">"Generate a Developer commits Activity Heatmap in current sprint of development team."</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700
        bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg shrink-0">
        <CustomButton
          variant="cancel"
          onClick={handleClose}
          className="ml-2"
        >
          {step === 1 ? 'Close' : 'Cancel'}
        </CustomButton>

        <div className="ml-auto flex items-center gap-3">
          {(step === 2 || !isFirstTimeSetup) && hasUnsavedChanges && (
            <UnsavedChangesIndicator
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}

          {step === 1 ? (
            <CustomButton
              onClick={handleConnect}
              disabled={githubSettingsLoading || !pat}
              isLoading={githubSettingsLoading}
            >
              {githubSettingsLoading ? 'Connecting…' : 'Connect'}
            </CustomButton>
          ) : (
            <CustomButton
              onClick={handleSaveChanges}
              disabled={githubSettingsLoading || !hasUnsavedChanges || !pat}
              isLoading={githubSettingsLoading}
            >
              {githubSettingsLoading ? 'Saving...' : 'Save Settings'}
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  )
}

export default GithubSettings;
